import React from "react";
import LearnerConfidenceInput from "../learner-confidence/LearnerConfidenceInput";
import PerceptualResponseInput from "../../primitives/perceptual-response/PerceptualResponseInput";

const humanizeConstruct = value => value
  .split("-")
  .map((part, index) => index === 0 ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : part)
  .join(" ");

export default function PairedScaleResponseInput({
  capability,
  learnerConfidenceCapability,
  state,
  primaryValue,
  confidenceValue,
  onPrimaryChange,
  onConfidenceChange,
  onSave,
  onLock,
  constructPresentations = {},
  categoricalOptions = [],
  continuousEndpointLabels,
  visuallyBlindContinuousValue = false,
  bridgeHelp,
}) {
  if (!capability || !state) return null;
  if (state.vectorComplete || state.currentScaleFamily === "complete") {
    return (
      <section className="av-paired-response" aria-label="Paired response complete">
        <p>All ratings submitted.</p>
        <p>Reference feedback is now available.</p>
      </section>
    );
  }

  const construct = capability.constructs?.[state.currentConstructIndex];
  const scaleFamily = state.currentScaleFamily;
  const domain = construct?.[scaleFamily];
  if (!construct || !domain) return null;
  const confidenceRequired = Boolean(
    capability.confidenceRequired?.[scaleFamily]
  );
  const canLock = primaryValue !== undefined && primaryValue !== "" &&
    (!confidenceRequired || confidenceValue !== undefined);
  const presentation = constructPresentations[construct.id] || {};
  const scalePresentation = presentation[scaleFamily] || {};
  const constructLabel = presentation.label || humanizeConstruct(construct.id);

  return (
    <section className="av-paired-response" aria-label="Paired categorical and continuous response">
      <header className="av-paired-response__header">
        <div>
          <p>Construct {state.currentConstructIndex + 1} of {capability.constructs.length}</p>
          <h3>{constructLabel}</h3>
        </div>
        <span className="av-paired-response__scale">Current scale: {scaleFamily}</span>
      </header>
      <p className="av-paired-response__status">
        {scaleFamily === "categorical"
          ? "First make a categorical judgment. The continuous scale follows after you submit this rating and its confidence."
          : "Now rate the same perceptual impression on the continuous scale."}
      </p>
      {bridgeHelp ? (
        <div className="av-paired-response__bridge-help">{bridgeHelp}</div>
      ) : null}
      <ol className="av-paired-response__sequence" aria-label="Paired response sequence">
        <li data-state={scaleFamily === "categorical" ? "current" : "complete"}>
          <span>1</span> Categorical judgment + confidence
        </li>
        <li data-state={scaleFamily === "continuous" ? "current" : "upcoming"}>
          <span>2</span> Continuous judgment + confidence
        </li>
      </ol>
      <PerceptualResponseInput
        constructLabel={constructLabel}
        instruction={presentation.instruction}
        responseFamilyLabel={scalePresentation.responseFamilyLabel}
        sourceParameterLabel={scalePresentation.sourceParameterLabel || domain.parameterId}
        options={categoricalOptions}
        scaleFamily={scaleFamily}
        min={domain.min}
        max={domain.max}
        integer={Boolean(domain.integer)}
        value={primaryValue}
        onChange={onPrimaryChange}
        endpointLabels={scaleFamily === "continuous" ? continuousEndpointLabels : undefined}
        visuallyBlindValue={scaleFamily === "continuous" && visuallyBlindContinuousValue}
        visualInitialValue={scaleFamily === "continuous" ? 0 : undefined}
      />
      <LearnerConfidenceInput
        capability={learnerConfidenceCapability}
        constructId={construct.id}
        scaleFamily={scaleFamily}
        value={confidenceValue}
        onChange={onConfidenceChange}
      />
      <div className="av-paired-response__actions">
        <button className="app-btn app-btn-primary" type="button" onClick={onSave} disabled={primaryValue === undefined || primaryValue === ""}>Save draft</button>
        <button className="app-btn app-btn-secondary" type="button" onClick={onLock} disabled={!canLock}>
          {scaleFamily === "categorical"
            ? "Continue to VAS rating"
            : state.currentConstructIndex < capability.constructs.length - 1
              ? "Continue to next quality"
              : "Submit ratings"}
        </button>
      </div>
      <p className="av-paired-response__status">Reference feedback is withheld until all required ratings are submitted.</p>
    </section>
  );
}
